/****************************************************************
 * File: pd-data-card.tsx
 * Description:
 * Author: zayfen <zhangyunfeng@pudutech.com>
 * Created: 2022/05/16 21:30
 *
 * History:
 *   Date               Programmer ID    Description
 *  -----------------  ---------------  ------------------------
 *  2022/05/16 21:30   zayfen           Init
 *
 * This unpublished material is proprietary to PuduTech.
 * All rights reserved. The methods and
 * techniques described herein are considered trade secrets
 * and/or confidential. Reproduction or distribution, in whole
 * or in part, is forbidden except by express written permission
 * of PuduTech.
 *****************************************************************/

import { useLocale } from 'next-intl'
import Image from 'next/image'
import { FC } from 'react'

export interface PdDataCardProps {
  label: string
  value: string
  icon?: string
  subtitle?: string
  type: 'Simple' | 'WithIcon'
  className?: string
}

const PdDataCard: FC<PdDataCardProps> = ({ label, value, type, icon, subtitle, className }) => {
  const locale = useLocale()

  return (
    <>
      {type === 'Simple' && (
        <div
          className={[
            'px-4 py-6 w-40 h-42',
            'laptop:w-72 laptop:h-72 laptop:px-8 laptop:py-12',
            'flex flex-col justify-between group transition-all duration-300 bg-pdbg hover:bg-pdblue overflow-hidden',
            className,
          ].join(' ')}
        >
          <h5
            className={[
              'text-sm h-10',
              'laptop:text-base laptop:h-12',
              'text-pd-gray group-hover:text-white relative line-clamp-2',
            ].join(' ')}
          >
            <span className="absolute top-0">{label}</span>
          </h5>

          <div className={['mt-4', 'laptop:mt-0'].join(' ')}>
            {subtitle && <span className={['text-sm', 'laptop:text-base', 'text-pd-gray'].join(' ')}>{subtitle}</span>}
            <p
              className={[
                ['nl'].includes(locale) ? 'laptop:text-54' : 'laptop:text-54.4',
                'text-32 font-bold text-black group-hover:text-white line-clamp-2 break-all',
              ].join(' ')}
            >
              {value}
            </p>
          </div>
        </div>
      )}

      {type === 'WithIcon' && (
        <div
          className={[
            'w-full min-h-20 p-4 justify-start',
            'laptop:flex-col laptop:justify-start laptop:w-72 laptop:!h-auto laptop:pt-8',
            'group aspect-square bg-white flex items-center',
            className,
          ].join(' ')}
        >
          {icon && (
            <div className={['relative flex-none', 'w-12 h-12 mr-6', 'laptop:w-24 laptop:h-24 laptop:mr-0'].join(' ')}>
              <Image
                src={icon}
                alt="pudutech pudurobotics data"
                layout="fill"
                objectFit={'contain'}
                objectPosition={'center'}
                quality={100}
                unoptimized
              />
            </div>
          )}

          <div className={['text-left', 'laptop:text-center laptop:pb-8 justify-self-end'].join(' ')}>
            <h5 className={['text-sm mb-0', 'laptop:text-xl laptop:mb-4', 'text-black/60'].join(' ')}>{label}</h5>
            <p
              className={[
                'text-2xl text-left leading-[unset]',
                'laptop:text-3xl laptop:text-center',
                'text-pdblue font-semibold break-all',
              ].join(' ')}
            >
              {value}
            </p>
          </div>
        </div>
      )}
    </>
  )
}

export default PdDataCard
